import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { taxZoneEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';

export type ZoneAppliesToOverride = 'all' | 'materials' | 'labor' | 'freight' | null;

export interface SetZoneOverridePayload {
  zipCode: string;
  rateOverride?: number | null;
  appliesToOverride?: ZoneAppliesToOverride;
}

/**
 * NOT-130 — Set or clear ZIP-level overrides on a TaxZoneIndex row.
 *
 * Independent fields:
 *   - omit a field → unchanged on the server
 *   - null         → clear that dimension (fall back to tax_code)
 *   - value        → set / replace
 *
 * The Revert button on the Overrides Viewer dispatches with BOTH fields
 * = null so the zone falls fully back to its tax_codes.
 */
export function useSetZoneOverride() {
  const qc = useQueryClient();
  return useApiMutation<void, SetZoneOverridePayload>({
    mutationFn: ({ zipCode, ...body }) =>
      apiPatch<void>(operationsClient, taxZoneEndpoints.zoneOverride(zipCode), body),
    onSuccess: (_data, vars) => {
      const cleared = vars.rateOverride === null && vars.appliesToOverride === null;
      toast.success(
        cleared
          ? `ZIP-level override cleared for ${vars.zipCode}.`
          : `Override saved for ZIP ${vars.zipCode}.`,
      );
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    },
  });
}
